import { useQuery } from "@tanstack/react-query";
import { getCurrentUser } from "@/api/users";

/**
 * Hook to fetch the current user with their role and permissions.
 * This calls /users/me which requires the user to be in users.yml.
 *
 * If the user is authenticated via Okta but not in users.yml,
 * this will return a 403 NO_ROLE_ASSIGNED error.
 *
 * Note: This is separate from useUserProfile which calls /user/me
 * and returns basic profile info that works for any authenticated user.
 */
export const useCurrentUser = () => {
  return useQuery({
    queryKey: ["users", "me"],
    queryFn: getCurrentUser,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on 403 (no role) or 401 (not authenticated)
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403 || status === 401) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Alias for backwards compatibility
export const useUserRole = useCurrentUser;
